import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getHistoricalHypnogram, hypnogramKeys, type AnalysisHistory, type AnalysisRun } from "@rekonas/data-access";
import { Alert, Spinner } from "@rekonas/ui";
import { ResultHypnogramPlot, stageOrder } from "../components/hypnogram/ResultHypnogramPlot";

function releaseName(run: AnalysisRun): string {
  return run.algorithm_release.display_name || run.algorithm_release.release_id;
}

function sameComparisonLineage(first: AnalysisRun, second: AnalysisRun): boolean {
  if (first.recording_ref !== second.recording_ref) return false;
  if (first.input_data_version === second.input_data_version &&
      first.input_manifest_sha256 === second.input_manifest_sha256) return true;
  // A retained run reads the signals artifact and stores its stages in the
  // complete artifact. Later recomputations can use that exact complete artifact.
  const outputIsInput = (retained: AnalysisRun, later: AnalysisRun) =>
    retained.output?.type === "scientific_artifact" &&
    retained.output.data_version === later.input_data_version &&
    retained.output.manifest_sha256 === later.input_manifest_sha256;
  return outputIsInput(first, second) || outputIsInput(second, first);
}

export function HypnogramComparison({ recording, history }: { recording: string; history: AnalysisHistory }) {
  const [open, setOpen] = useState(false);
  const [firstChoice, setFirstChoice] = useState("");
  const [secondChoice, setSecondChoice] = useState<string | null>(null);
  const [cursor, setCursor] = useState<number | null>(null);
  const completed = history.runs.filter((run) => run.status === "complete" && run.output);
  const currentRef = history.current?.run.run_ref;
  const preferredFirst = completed.find((run) => run.run_ref === currentRef) || completed[0];
  const first = completed.find((run) => run.run_ref === firstChoice) || preferredFirst;
  const preferredSecond = completed.find((run) => first && run.run_ref !== first.run_ref &&
    sameComparisonLineage(first, run));
  const second = secondChoice === null ? preferredSecond :
    completed.find((run) => run.run_ref === secondChoice && run.run_ref !== first?.run_ref);
  const sameLineage = !first || !second || sameComparisonLineage(first, second);
  const firstResult = useQuery({ queryKey: hypnogramKeys.historical(recording, first?.run_ref || ""),
    queryFn: ({ signal }) => getHistoricalHypnogram(recording, first!.run_ref, signal),
    enabled: open && Boolean(first), retry: false });
  const secondResult = useQuery({ queryKey: hypnogramKeys.historical(recording, second?.run_ref || ""),
    queryFn: ({ signal }) => getHistoricalHypnogram(recording, second!.run_ref, signal),
    enabled: open && Boolean(second && sameLineage), retry: false });
  const duration = firstResult.data?.duration_seconds || history.duration_seconds || 0;
  const comparable = sameLineage && (!secondResult.data || secondResult.data.duration_seconds === duration);
  const order = stageOrder(firstResult.data?.result, secondResult.data?.result);
  function valid(run: AnalysisRun | undefined, result: typeof firstResult.data) {
    return Boolean(run && result && result.run_ref === run.run_ref &&
      result.input_data_version === run.input_data_version &&
      result.input_manifest_sha256 === run.input_manifest_sha256);
  }
  return <section className="operator-panel hypnogram-comparison" aria-label="Compare hypnograms">
    <details onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary>Compare hypnograms</summary>
      <p className="operator-muted">Completed saved results on one recording timeline. Viewing does not recompute or promote a run.</p>
      {!completed.length && <p>No completed hypnograms are available.</p>}
      {first && <div className="operator-fields">
        <label>First run <select aria-label="First comparison run" value={first.run_ref} onChange={(event) => { setFirstChoice(event.target.value); setSecondChoice(null); setCursor(null); }}>
          {completed.map((run) => <option key={run.run_ref} value={run.run_ref}>{releaseName(run)} · {run.run_ref}</option>)}
        </select></label>
        <label>Second run <select aria-label="Second comparison run" value={second?.run_ref || ""} onChange={(event) => { setSecondChoice(event.target.value); setCursor(null); }}>
          <option value="">One result only</option>
          {completed.filter((run) => run.run_ref !== first.run_ref).map((run) => <option key={run.run_ref} value={run.run_ref}>{releaseName(run)} · {run.run_ref}</option>)}
        </select></label>
      </div>}
      {!sameLineage && <Alert>These runs use different input signal artifacts or timeline lineage. Choose runs from the same input to compare them.</Alert>}
      {sameLineage && !comparable && <Alert>These results report different recording durations. Their timelines cannot be aligned safely.</Alert>}
      {!duration && first && <Alert>The recording duration is unavailable; the plots cannot be aligned to its timeline.</Alert>}
      {(firstResult.isPending && first || secondResult.isPending && second && sameLineage) && open && <p role="status"><Spinner /> Loading saved hypnograms…</p>}
      {(firstResult.isError || secondResult.isError && sameLineage) && <Alert>Could not read a saved hypnogram. Refresh the run selection and try again.</Alert>}
      {duration > 0 && valid(first, firstResult.data) && <div className="hypnogram-comparison-plot">
        <h4>{releaseName(first!)} · <code title={first!.run_ref}>{first!.run_ref}</code> {currentRef === first!.run_ref && <span className="product-status status-ready">Promoted / current</span>}</h4>
        <ResultHypnogramPlot result={firstResult.data!.result} durationSeconds={duration} order={order}
          cursorSeconds={cursor} onCursorChange={setCursor} />
      </div>}
      {duration > 0 && comparable && valid(second, secondResult.data) && <div className="hypnogram-comparison-plot">
        <h4>{releaseName(second!)} · <code title={second!.run_ref}>{second!.run_ref}</code> {currentRef === second!.run_ref && <span className="product-status status-ready">Promoted / current</span>}</h4>
        <ResultHypnogramPlot result={secondResult.data!.result} durationSeconds={duration} order={order}
          cursorSeconds={cursor} onCursorChange={setCursor} />
      </div>}
    </details>
  </section>;
}
